import { EHttpMethods } from "../../../../enums";
import { getToken } from "../../../context/authContext";
import { ApiServiceManager, fetchRequest } from "../../apiServiceManager";
import { CASHOUT_BANKS_URL } from "./constants";
import {
  ICashoutBankBaseResponse,
  ICashoutBankListResponse,
  ICashoutBankResponse,
  ICreateCashoutBankBody,
  IUpdateCashoutBankBody,
} from "./models";

const defaultProvide = async () => await getToken();

/** Soft-deleted banks are never returned. */
export const getCashoutBanks = (
  page = 0,
  size = 10
): Promise<ICashoutBankListResponse> => {
  const url = `${CASHOUT_BANKS_URL}?page=${page}&size=${size}&sort=createdAt,desc`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/**
 * The bank cashouts are currently paid out from. Rejects with a 404 payload when none is enabled —
 * a normal state, not a failure, so callers treat it as "no active bank".
 */
export const getActiveCashoutBank = (): Promise<ICashoutBankResponse> => {
  const url = `${CASHOUT_BANKS_URL}/active`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getCashoutBank = (id: number): Promise<ICashoutBankResponse> => {
  const url = `${CASHOUT_BANKS_URL}/${id}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const createCashoutBank = (
  body: ICreateCashoutBankBody
): Promise<ICashoutBankResponse> => {
  const descriptor = new ApiServiceManager(
    CASHOUT_BANKS_URL,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

/** Only the fields present on `body` are applied; omitted fields keep their current value. */
export const updateCashoutBank = (
  id: number,
  body: IUpdateCashoutBankBody
): Promise<ICashoutBankResponse> => {
  const url = `${CASHOUT_BANKS_URL}/${id}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

/**
 * Enabling a bank atomically disables whichever one was active before, so only one is ever active.
 * Disabling leaves none enabled and the connector falls back to its own configuration.
 */
export const setCashoutBankEnabled = (
  id: number,
  enabled: boolean
): Promise<ICashoutBankResponse> => {
  const url = `${CASHOUT_BANKS_URL}/${id}/enabled`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, { enabled });
};

/** Soft-delete; the bank is also disabled, so a deleted bank can never remain the active one. */
export const deleteCashoutBank = (
  id: number
): Promise<ICashoutBankBaseResponse> => {
  const url = `${CASHOUT_BANKS_URL}/${id}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.DELETE,
    defaultProvide
  );
  return fetchRequest(descriptor);
};
